import { AlertTitle, Collapse } from "@mui/material"
import { useConfirm } from "material-ui-confirm"
import { ReactElement, useEffect, useState } from "react"

import CommentDisplayComponent, { CommentDivider } from "./commentDisplay"
import SubscriptionButton from "./subscriptionButton"
import { IAppError } from "../../../errors/errorTypes"
import { IComment } from "../../../services/middleware/mappers/singleDeviceMapper"
import AppAlert from "../../dataDisplay/alerts/appAlert"
import CommentSkeleton from "../../loading/skeletons/commentSkeleton"
import { AppCard, AppCardContent, AppCardHeader } from "../../surfaces/appCard"
import DisplayIf from "../../wrappers/displayIf"

interface IGenericCommentsCardProps {
    fetchingComments: boolean
    deleteError: IAppError | undefined
    deleteLoading: boolean
    postDelete: (commentId: string) => Promise<void>
    createError: IAppError | undefined
    createLoading: boolean
    pinComment?: (commentId: string) => Promise<void>
    unpinComment?: () => Promise<void>
    pinError?: IAppError
    pinLoading?: boolean
    canCreateComments: boolean
    canViewComments?: boolean
    canPinComments?: boolean
    mutate: () => void
    pinnedCommentId?: string
    comments: IComment[] | undefined
    renderCreateComponent: (isLoading: boolean) => ReactElement
    title: string
    subheader: string
    canDeleteComment?: (comment: IComment) => boolean
    isSubscribed?: boolean
    deviceSysRef?: string
    deviceType?: string
}

/**
 * Returns a generic comments card. Handles the basic functionality and styling of a comment thread.
 * Any context-specific functions are required as props.
 *
 * Requires a Functional component prop for rendering the component responsible for adding comments
 */
export const GenericCommentsCard = ({
    fetchingComments,
    deleteError,
    deleteLoading,
    postDelete,
    createError,
    createLoading,
    pinComment,
    unpinComment,
    pinError,
    pinLoading = false,
    canCreateComments,
    canViewComments = true,
    canPinComments = false,
    canDeleteComment,
    mutate,
    pinnedCommentId,
    comments,
    renderCreateComponent,
    title,
    subheader,
    isSubscribed = false,
    deviceSysRef,
    deviceType,
}: IGenericCommentsCardProps): ReactElement => {
    const confirm = useConfirm()

    const isLoading =
        fetchingComments || deleteLoading || createLoading || pinLoading

    const [errorToDisplay, setErrorToDisplay] = useState<
        IAppError | undefined
    >()

    const unpinnedComments = comments?.filter(
        (comment) => comment.id !== pinnedCommentId
    )

    const pinnedComment = comments?.find(
        (comment) => comment.id === pinnedCommentId
    )

    useEffect(() => {
        setErrorToDisplay(deleteError)
    }, [deleteError, deleteLoading])

    useEffect(() => {
        setErrorToDisplay(createError)
    }, [createError, createLoading])

    useEffect(() => {
        setErrorToDisplay(pinError)
    }, [pinError, pinLoading])

    const createComponent = renderCreateComponent(isLoading)

    const deleteComment =
        (comment: IComment): (() => void) =>
        () =>
            confirm({
                description:
                    "This will permanently delete the comment " +
                    `'${comment.comment}'.`,
            })
                .then(async () => {
                    postDelete(comment.id).then(mutate)
                })
                .catch(() => {
                    // ...
                })

    const unpin = (): void => {
        if (unpinComment !== undefined) {
            unpinComment().then(mutate)
        }
    }

    const pin = (commentId: string): void => {
        if (pinComment !== undefined) {
            pinComment(commentId).then(mutate)
        }
    }

    const canDelete = (comment: IComment): boolean | undefined => {
        if (canDeleteComment) {
            return canDeleteComment(comment)
        }
        return undefined
    }

    if (!canViewComments) {
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>
    }

    return (
        <AppCard>
            {deviceSysRef ? (
                <AppCardHeader
                    title={title}
                    subheader={subheader}
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                    action={
                        <SubscriptionButton
                            deviceSysRef={deviceSysRef}
                            deviceType={deviceType}
                            initialIsSubscribed={isSubscribed}
                        />
                    }
                />
            ) : (
                <AppCardHeader
                    title={title}
                    subheader={subheader}
                    titleTypographyProps={{
                        variant: "h5",
                        fontWeight: "500",
                    }}
                />
            )}

            <AppCardContent
                style={{ display: "flex", flexDirection: "column" }}
            >
                <Collapse
                    style={{ marginBottom: "16px" }}
                    in={!isLoading && !!errorToDisplay}
                    mountOnEnter
                >
                    <AppAlert severity={errorToDisplay?.level}>
                        <AlertTitle>{errorToDisplay?.message}</AlertTitle>
                        {errorToDisplay?.displayDetail}
                    </AppAlert>
                </Collapse>
                {pinnedComment && (
                    <CommentDisplayComponent
                        comment={pinnedComment}
                        isLoading={isLoading}
                        deleteComment={deleteComment(pinnedComment)}
                        togglePin={() => unpin()}
                        isPinned
                        canPinComment={canPinComments}
                        canDeleteComment={canDelete(pinnedComment)}
                    />
                )}
                {unpinnedComments &&
                    unpinnedComments.length > 0 &&
                    unpinnedComments
                        .sort(
                            (a, b) =>
                                new Date(a.publishedAt).getTime() -
                                new Date(b.publishedAt).getTime()
                        )
                        .map((comment) => (
                            <CommentDisplayComponent
                                key={comment.id}
                                comment={comment}
                                deleteComment={deleteComment(comment)}
                                isLoading={isLoading}
                                togglePin={() => pin(comment.id)}
                                canPinComment={canPinComments && !pinnedComment}
                                canDeleteComment={canDelete(comment)}
                            />
                        ))}
                <DisplayIf condition={isLoading && !deleteLoading}>
                    <>
                        <CommentSkeleton />
                        <CommentDivider />
                    </>
                </DisplayIf>

                {canCreateComments && createComponent}
            </AppCardContent>
        </AppCard>
    )
}

GenericCommentsCard.defaultProps = {
    pinComment: undefined,
    unpinComment: undefined,
    pinnedCommentId: undefined,
    pinError: undefined,
    pinLoading: false,
    canPinComments: false,
    canViewComments: true,
    canDeleteComment: undefined,
    isSubscribed: false,
    deviceType: undefined,
    deviceSysRef: undefined,
}
